"use client";

import { useEffect, useRef, useState } from "react";

import type { StoryChapter } from "@/lib/stories";
import { cn } from "@/lib/utils";

import { formatTime } from "./format-time";
import { useStoryStage } from "./story-stage";

export interface StoryFact {
  label: string;
  value: string;
}

export interface StorySection {
  title: string;
  url: string;
}

interface StoryRailProps {
  facts: StoryFact[];
  chapters: StoryChapter[];
  /** Article headings, used in place of chapters until a story is filmed. */
  sections: StorySection[];
  runtime?: string;
  shareUrl: string;
  shareTitle: string;
}

const CARD =
  "border-marketing-border-light bg-marketing-cream-light rounded-2xl border dark:border-white/10 dark:bg-white/[0.03]";
const LABEL =
  "text-marketing-text-secondary font-mono text-[0.625rem] tracking-[0.12em] uppercase dark:text-neutral-500";
const SHARE_BUTTON =
  "border-marketing-border-light text-marketing-text-muted hover:border-marketing-blue hover:text-marketing-blue flex size-[34px] items-center justify-center rounded-full border transition-colors dark:border-white/15 dark:text-neutral-400";

export default function StoryRail({
  facts,
  chapters,
  sections,
  runtime,
  shareUrl,
  shareTitle,
}: StoryRailProps) {
  const { activeChapter, jumpTo } = useStoryStage();
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(
    () => () => {
      clearTimeout(copyTimer.current);
    },
    [],
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      copyTimer.current = setTimeout(() => {
        setCopied(false);
      }, 1800);
    } catch {
      // Clipboard access is denied outside a secure context; the address bar is
      // the fallback and a thrown error here would take the page with it.
    }
  };

  return (
    <aside className="flex flex-col gap-5 lg:sticky lg:top-28 lg:self-start">
      <div className={CARD}>
        <div className="p-6">
          <p className={LABEL}>In this story</p>
          <dl className="mt-5 space-y-5">
            {facts.map((fact) => (
              <div key={fact.label}>
                <dt className={LABEL}>{fact.label}</dt>
                <dd className="text-foreground mt-1.5 text-[0.9375rem] leading-snug">
                  {fact.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {chapters.length > 0 ? (
        <div className={CARD}>
          <div className="p-6">
            <div className="flex items-baseline justify-between gap-4">
              <p className={LABEL}>Chapters</p>
              {runtime && (
                <span className="text-marketing-text-muted font-mono text-[0.6875rem] dark:text-neutral-400">
                  {runtime}
                </span>
              )}
            </div>

            <ul className="mt-3">
              {chapters.map((chapter, index) => (
                <li
                  key={chapter.time}
                  className="border-marketing-border-light border-t dark:border-white/10"
                >
                  <button
                    type="button"
                    onClick={() => {
                      jumpTo(chapter.time);
                    }}
                    className="group flex w-full items-baseline gap-4 py-3 text-left"
                  >
                    <span
                      className={cn(
                        "font-mono text-[0.6875rem] tabular-nums transition-colors",
                        index === activeChapter
                          ? "text-marketing-blue"
                          : "text-marketing-text-secondary dark:text-neutral-500",
                      )}
                    >
                      {formatTime(chapter.time)}
                    </span>
                    <span
                      className={cn(
                        "group-hover:text-marketing-blue text-[0.9375rem] leading-snug transition-colors",
                        index === activeChapter
                          ? "text-marketing-blue"
                          : "text-foreground",
                      )}
                    >
                      {chapter.label}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            <p className="text-marketing-text-secondary mt-3 text-[0.8125rem] dark:text-neutral-500">
              Tap a chapter to jump into the video.
            </p>
          </div>
        </div>
      ) : (
        sections.length > 0 && (
          <div className={CARD}>
            <div className="p-6">
              <p className={LABEL}>Sections</p>
              <ul className="mt-3">
                {sections.map((section) => (
                  <li
                    key={section.url}
                    className="border-marketing-border-light border-t dark:border-white/10"
                  >
                    <a
                      href={section.url}
                      className="text-foreground hover:text-marketing-blue block py-3 text-[0.9375rem] leading-snug transition-colors"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      )}

      <div className="flex items-center gap-3">
        <span className={LABEL}>Share</span>
        {/* The confirmation belongs on the control that was pressed, not on the
            label beside it: a word that swaps to "Copied" and back reads as the
            heading changing its mind. The icon answers instead. */}
        <button
          type="button"
          onClick={() => {
            void copyLink();
          }}
          aria-label={copied ? "Link copied" : "Copy link to this story"}
          className={cn(
            SHARE_BUTTON,
            copied && "border-marketing-blue text-marketing-blue",
          )}
        >
          <span className="relative flex size-[15px] items-center justify-center">
            <svg
              width="15"
              height="15"
              viewBox="0 0 20 20"
              aria-hidden
              fill="none"
              className={cn(
                "absolute transition-[opacity,transform] duration-200 ease-out",
                copied ? "scale-50 opacity-0" : "scale-100 opacity-100",
              )}
            >
              <path
                d="M8.5 11.5a3.5 3.5 0 0 0 5 0l2-2a3.5 3.5 0 0 0-5-5l-1 1m-2 4a3.5 3.5 0 0 0-5 0l-2 2a3.5 3.5 0 0 0 5 5l1-1"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <svg
              width="15"
              height="15"
              viewBox="0 0 20 20"
              aria-hidden
              fill="none"
              className={cn(
                "absolute transition-[opacity,transform] duration-200 ease-out",
                copied ? "scale-100 opacity-100" : "scale-50 opacity-0",
              )}
            >
              <path
                d="m4.5 10.5 3.5 3.5 7.5-7.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
        <a
          href={`https://x.com/intent/post?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share this story on X"
          className={SHARE_BUTTON}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            aria-hidden
            fill="currentColor"
          >
            <path d="M18.9 2H22l-6.8 7.8L23 22h-6.4l-5-6.5L5.8 22H2.7l7.2-8.3L1.5 2H8l4.7 6.1L18.9 2Zm-1.1 18h1.7L6.3 3.7H4.5L17.8 20Z" />
          </svg>
        </a>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share this story on LinkedIn"
          className={SHARE_BUTTON}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            aria-hidden
            fill="currentColor"
          >
            <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9.5h4v11H3v-11Zm7 0h3.8v1.5h.06c.53-.95 1.82-1.8 3.64-1.8 2.9 0 4.5 1.77 4.5 5.1v6.2h-4v-5.5c0-1.4-.5-2.35-1.75-2.35-1.06 0-1.7.72-1.98 1.42-.1.25-.13.6-.13.95v5.48h-4v-11Z" />
          </svg>
        </a>
      </div>
    </aside>
  );
}
